// Copilot Constants - Centralized configuration for business-critical values
// This file contains configurable values that are used across multiple components

export const COPILOT_CONSTANTS = {
  // Message Configuration - Used across multiple components
  MESSAGES: {
    WELCOME: 'Hello, I am MCAPSHelp Copilot. How can I help you today?',
    PLACEHOLDER: 'Ask a question or raise a request',
    DISCLAIMER: 'AI-generated content may be incorrect',
    FALLBACK_PLACEHOLDER: 'Ask a question...',
    NEW_CONVERSATION: 'New conversation', // Legacy newConversation constant
    SYSTEM_ISSUE: 'We are currently facing a technical issue that is preventing us from retrieving the requested information. Kindly try again after some time.',
    ERROR_FALLBACK: 'Something went wrong. Please try again later.',
    INTERNAL_ONLY: 'Microsoft Internal Only',
  },

  // Input Limits - Used in validation and UI
  INPUT_LIMITS: {
    MAX_CHARACTER_COUNT: 4000,
    DEFAULT_MAX_COUNT: 1000
  },

  // Message Roles - Used for message typing and validation
  MESSAGE_ROLES: {
    USER: 'user',
    ASSISTANT: 'assistant',
    SYSTEM: 'system',
    ERROR: 'error'
  },

  // Message ID Prefixes - Used for message identification
  MESSAGE_ID_PREFIXES: {
    USER: 'u-',
    ASSISTANT: 'a-',
    SYSTEM: 's-',
    ERROR: 'e-',
    WELCOME: 'welcome',
    WELCOME_REFRESH: 'welcome-refresh'
  },

  // Session Configuration - Used for API calls and telemetry
  SESSION: {
    ID_PREFIX: 'sess-',
    ID_LENGTH: 36 // Used with Math.random().toString(36).slice(2)
  },

  // API Configuration - Used in multiple API calls
  API: {
    SUCCESS_STATUS_MIN: 200,
    SUCCESS_STATUS_MAX: 300,
    ENDPOINTS: {
      FILE_UPLOAD: 'api/McapsHelpBotTicket/UploadTicketAttachments',
      FORM_ATTACHMENT: 'api/ragquery/attachment'
    },
    ERROR_MESSAGES: {
      API_NOT_CONFIGURED: 'API URL is not configured',
      API_ERROR_PREFIX: 'Copilot API error: '
    },
    RESET_QUERY: 'reset'
  },

  // Limits for various UI lists
  LIST_LIMITS: {
    MAX_SOURCES: 5,
    REFERENCE_V2_MAX_VISIBLE: 4,
    REFERENCE_V2_MIN_VISIBLE: 1,
    MAX_SUGGESTIONS: 6
  },

  // Theme Configuration - Used across components
  THEME: {
    DEFAULT_MODE: 'light',
    MODES: ['light', 'dark']
  },

  // Feature Flags - Business features that can be toggled
  FEATURES: {
    SHOW_DISCLAIMER: true,
    SHOW_ATTACHMENT: true,
    ENABLE_FOLLOW_UP_QUESTIONS: true,
    AUTO_SCROLL_TO_BOTTOM: true,
    USE_MOCK_API: false, // Toggle to use local ApiResponse.json instead of real API for testing
    ENABLE_WELCOME_SUGGESTIONS: true, // Enable configurable suggestion questions in welcome prompt
    SHOW_EXIT_TOGGLE: false // Show Exit toggle button to cancel form sessions
  },

  // Ticket Pattern Configuration - Easily extensible ticket format definitions
  // Add new ticket patterns here without modifying extraction logic
  TICKET_PATTERNS: [
    {
      name: 'INC',
      description: 'Incident ticket format (INC12345678)',
      pattern: /INC\d{8}/g,
      normalize: (match: string) => match.toUpperCase()
    },
    {
      name: 'TKT',
      description: 'Standard ticket format (TKT-1234567-XXXXX)',
      pattern: /TKT-\d{7}-[A-Z0-9]{5}/gi,
      normalize: (match: string) => match.toUpperCase()
    },
    {
      name: 'CASE',
      description: 'Case number format (5-0000014111035)',
      pattern: /\d-\d{13}/g,
      normalize: (match: string) => match
    }
  ] as const
} as const;

// Default Values - Used as fallbacks in multiple components
export const DEFAULT_VALUES = {
  THEME_MODE: COPILOT_CONSTANTS.THEME.DEFAULT_MODE,
  MAX_COUNT: COPILOT_CONSTANTS.INPUT_LIMITS.MAX_CHARACTER_COUNT,
  PLACEHOLDER_TEXT: COPILOT_CONSTANTS.MESSAGES.PLACEHOLDER,
  USER_EMAIL: ''
} as const;